/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { forwardRef, useCallback, type Ref } from 'react';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { LinkMenuItem, type LinkMenuItemProps } from '@atlaskit/side-nav-items/link-menu-item';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

export type LinkMenuItemWithAnalyticsProps<
	RouterLinkConfig extends Record<string, unknown> = never,
> = LinkMenuItemProps<RouterLinkConfig> & {
	// TODO BLU-5544 make `analytics` non-optional, after all Cosmos team usages of Nav4MenuLinkItem are updated (to pass analytics= prop)
	analytics?: {
		actionSubjectId: string;
		itemId: string;
		level: number;
		[attribute: string]: boolean | number | string | undefined;
	};
};

function LinkMenuItemWithAnalytics<RouterLinkConfig extends Record<string, unknown> = never>(
	{ analytics, onClick, ...rest }: LinkMenuItemWithAnalyticsProps<RouterLinkConfig>,
	ref: Ref<HTMLAnchorElement>,
) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick: LinkMenuItemWithAnalyticsProps['onClick'] = useCallback<
		NonNullable<LinkMenuItemWithAnalyticsProps['onClick']>
	>(
		(event, analyticsEvent) => {
			onClick?.(event, analyticsEvent);

			if (!analytics) {
				return;
			}

			const { actionSubjectId, ...analyticsAttributes } = analytics;

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
				}),
				actionSubjectId,
				analyticsAttributes,
			);
		},
		[analytics, createAnalyticsEvent, onClick],
	);

	return <LinkMenuItem<RouterLinkConfig> ref={ref} onClick={handleClick} {...rest} />;
}

// oxlint-disable-next-line typescript/consistent-type-assertions
export default forwardRef(LinkMenuItemWithAnalytics) as <
	RouterLinkConfig extends Record<string, unknown> = never,
>(
	props: LinkMenuItemWithAnalyticsProps<RouterLinkConfig> & {
		ref?: Ref<HTMLAnchorElement>;
	},
) => ReturnType<typeof LinkMenuItem>;
